import { assert, error, log } from '../app/logger.js';
import { FoodMenu } from '../data/food_menu.js';
import { HTMLControl } from '../controls/html_control.js';
import { pipeUI } from '../ui/pipe_ui.js';
import { listenToEvent } from '../util/listen_to_event.js';
import { window } from '../window.js';

import './food_menu_control.js';

class FoodMenuView extends HTMLControl {
  static get selectors() {
    const Menus = 'food-menu-control';
    return {
      Menus,
      MenuAt: i => `${Menus}:nth-of-type(${i + 1})`,
      FoodDatalist: '#food-menu-foods',
      UnitDatalist: '#food-menu-units',
    };
  }

  static elements = {
    foodDatalist: this.selectors.FoodDatalist,
    unitDatalist: this.selectors.UnitDatalist,
    menuElements: this.selectors.Menus,
  };

  set(menu) {
    if (this.#menu == menu) {
      return this.sync();
    }

    this.#clear();
    this.#lazyInitializeIfNotYet();

    this.#menu = menu;

    this.#datesPipe = this.#menu.pipeables.dates.pipe({
      array: (list, diff) =>
        log(
          `[food-menu] sync days for ${diff
            .map(e => `${e.what} at ${e.idx} ${e.is || e.was}`)
            .join(', ')}`
        ),
      changed: entry => this.#changeMenuDate(entry.idx, entry.is),
      deleted: entry => this.#deleteMenu(entry.idx),
      inserted: entry => this.#insertMenu(entry.idx, entry.is),
    });
  }

  async sync() {
    if (!this.#menu) {
      error(`[food-menu] no food menu set`);
      return;
    }

    log(`[food-menu] sync ${this.#menu.id} food menu`);
    return this.#menu.loadIfNeeded();
  }

  static markup = `
    <datalist id='food-menu-units'></datalist>
    <datalist id='food-menu-foods'></datalist>
  `;

  #lazyInitializeIfNotYet() {
    if (this.#lazyInitialized) {
      return;
    }
    this.#lazyInitialized = true;

    // Units datalist.
    let unitOptions = FoodMenu.units.reduce(
      (a, v) => `${a}<option>${v}</option>`,
      ''
    );
    this.unitDatalist.innerHTML = unitOptions;

    // Foods datalist.
    pipeUI({
      pipeable: FoodMenu.foods,
      parentNode: this.foodDatalist,
      uiFunc: v => `<option>${v}</option>`,
    });

    // Update events.
    listenToEvent(this, 'food-add', async ev => {
      const day = this.#dayOf(ev.target);
      const meal = ev.detail.meal;

      let pipe = this.#foodListPipes.get(day)[meal];
      await pipe.closure(() =>
        this.#menu.addMenuItem(day, meal, ev.detail.index)
      );
      this.#fireChangeEvent();
    });

    listenToEvent(this, 'food-change', async ev => {
      const day = this.#dayOf(ev.target);
      const meal = ev.detail.meal;

      let pipe = this.#foodListPipes.get(day)[meal];
      await pipe.closure(() =>
        this.#menu.changeMenuItem(day, meal, ev.detail.index, ev.detail)
      );
      this.#fireChangeEvent();
    });

    listenToEvent(this, 'food-delete', async ev => {
      const day = this.#dayOf(ev.target);
      const meal = ev.detail.meal;

      let pipe = this.#foodListPipes.get(day)[meal];
      await pipe.closure(() =>
        this.#menu.deleteMenuItem(day, meal, ev.detail.index)
      );
      this.#fireChangeEvent();
    });
  }

  #syncFoodList(menuEl, meal, list, diff) {
    log(
      `[food-menu] #syncFoodList for ${diff
        .map(e => `${e.what} at ${e.idx} ${JSON.stringify(e.is || e.was)}}`)
        .join(', ')}`
    );
    for (let diffEntry of diff) {
      switch (diffEntry.what) {
        case 'changed':
          menuEl.changeFoodList(meal, diffEntry.idx, diffEntry.is);
          break;

        case 'deleted':
          menuEl.deleteFoodList(meal, diffEntry.idx);
          break;

        case 'inserted':
          menuEl.insertFoodList(meal, diffEntry.idx, diffEntry.is);
          break;
      }
    }
  }

  #changeMenuDate(index, date) {
    let menuEl = this.children[index];
    assert(menuEl, `[food-menu] no menu at ${index}`);
    if (menuEl) {
      menuEl.setDate(date);
    }
  }

  #deleteMenu(index) {
    let menuEl = this.children[index];
    assert(menuEl, `[food-menu] no menu at ${index}`);
    if (menuEl) {
      menuEl.remove();
    }
    this.#foodListPipes.delete(index);
  }

  #insertMenu(index, date) {
    let menuEl = window.document.createElement('food-menu-control');
    this.insertBefore(menuEl, this.children[index]);
    menuEl.setDate(date);

    let pipes = {};
    this.#foodListPipes.set(index, pipes);
    for (let meal of FoodMenu.meals) {
      let pipe = this.#menu.pipeables
        .getFoodList(index, meal)
        .pipe((...args) => this.#syncFoodList(menuEl, meal, ...args));
      pipes[meal] = pipe;
    }
  }

  #clear() {
    // Do #unitsPipe
    if (this.#datesPipe) {
      this.#datesPipe.unpipe();
    }
    for (let menuEl of this.menuElements) {
      menuEl.remove();
    }
    this.#foodListPipes.clear();
  }

  #dayOf(menuEl) {
    return Array.from(this.children).indexOf(menuEl);
  }

  #fireChangeEvent() {
    this.ownerDocument.dispatchEvent(new Event('food-menu-change'));
  }

  #datesPipe;
  #foodListPipes = new Map();
  #lazyInitialized = false;
  #menu;
}

window.customElements.define('food-menu-view', FoodMenuView);

export { FoodMenuView };
